import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  commandUnitSchema,
  LocationSchema,
  nonFutureDateTimeSchema,
  OrganisationRoleSchema,
  shortTextSchema,
  yesNoSchema,
  yesOnlySchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "userIsAttendingOfficer", // 136-detention-1.1
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("detentionDetails.userIsAttendingOfficer"),
    requiredLabel: tk("detentionDetails.userIsAttendingOfficerRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "createdOnBehalfOfAnotherOfficerWithShoulderNumber", // 136-detention-1.2
    type: "officerLookup",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    label: tk(
      "detentionDetails.createdOnBehalfOfAnotherOfficerWithShoulderNumber",
    ),
    requiredLabel: tk(
      "detentionDetails.createdOnBehalfOfAnotherOfficerRequired",
    ),
    isVisible: ({ formValues }) =>
      match(formValues.userIsAttendingOfficer)
        .with({ value: "no", valueLabel: P._ }, () => true)
        .with(undefined, { value: "yes", valueLabel: P._ }, () => false)
        .exhaustive(),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "createdOnBehalfOfAnotherOfficerBanner", // 136-detention-1.3
    type: "banner",
    bannerType: "info",
    body: tk("detentionDetails.createdOnBehalfOfAnotherOfficerBanner"),
    isVisible: ({ formValues }) =>
      match(formValues.userIsAttendingOfficer)
        .with({ value: "no", valueLabel: P._ }, () => true)
        .with(undefined, { value: "yes", valueLabel: P._ }, () => false)
        .exhaustive(),
  })
  .withFieldAlwaysVisible({
    fieldName: "dateTimeOfDetention", // 136-detention-2.1
    type: "date-time",
    timeLabel: tk("detentionDetails.timeOfDetention"),
    dateLabel: tk("detentionDetails.dateOfDetention"),
    requiredLabel: tk("detentionDetails.dateTimeOfDetentionRequired"),
    disableFuture: true,
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
  })
  .withFieldAlwaysVisible({
    fieldName: "locationDetentionTookPlace", // 136-detention-3.1
    type: "location",
    ...new FieldBuilder().withSchema(LocationSchema).build(),
    label: tk("detentionDetails.locationDetentionTookPlace"),
    requiredLabel: tk("detentionDetails.locationDetentionTookPlaceRequired"),
  })
  .withFieldAlwaysVisible({
    fieldName: "confirmPersonAddress", // 136-detention-3.2
    type: "checkbox",
    // Optional for now
    ...new FieldBuilder()
      .withNullableSchema(yesOnlySchema)
      .captureValueLabel()
      .build(),
    label: tk("createIncident.outsideAboveAddress"),
  })
  .withReadonlyFieldAlwaysVisible({
    fieldName: "incidentLocationBanner", // 136-detention-3.3
    type: "banner",
    body: tk("createIncident.incidentLocationBanner"),
    bannerType: "info",
  })
  .withFieldAlwaysVisible({
    fieldName: "orgResponsibleForDetention", // 136-detention-4.1
    type: "organisation",
    filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
    ...new FieldBuilder()
      .withSchema(commandUnitSchema)
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.orgResponsibleForDetention"),
    requiredLabel: tk("detentionDetails.orgResponsibleForDetentionRequired"),
  })
  .withFieldAlwaysVisible({
    fieldName: "londonBoroughDetentionTookPlace", // 136-detention-4.2
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "barkingAndDagenham" as const,
          "barnet" as const,
          "bexley" as const,
          "brent" as const,
          "bromley" as const,
          "camden" as const,
          "croydon" as const,
          "ealing" as const,
          "enfield" as const,
          "greenwich" as const,
          "hackney" as const,
          "hammersmithAndFulham" as const,
          "haringey" as const,
          "harrow" as const,
          "havering" as const,
          "hillingdon" as const,
          "hounslow" as const,
          "islington" as const,
          "kensingtonAndChelsea" as const,
          "kingstonUponThames" as const,
          "lambeth" as const,
          "lewisham" as const,
          "merton" as const,
          "newham" as const,
          "redbridge" as const,
          "richmondUponThames" as const,
          "southwark" as const,
          "sutton" as const,
          "towerHamlets" as const,
          "walthamForest" as const,
          "wandsworth" as const,
          "cityOfWestminster" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.londonBoroughTookPlace.detention.title"),
    requiredLabel: tk(
      "detentionDetails.londonBoroughTookPlace.detention.required",
    ),
    options: [
      {
        value: "barkingAndDagenham",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.barkingAndDagenham",
        ),
      },
      {
        value: "barnet",
        label: tk("detentionDetails.londonBoroughTookPlace.options.barnet"),
      },
      {
        value: "bexley",
        label: tk("detentionDetails.londonBoroughTookPlace.options.bexley"),
      },
      {
        value: "brent",
        label: tk("detentionDetails.londonBoroughTookPlace.options.brent"),
      },
      {
        value: "bromley",
        label: tk("detentionDetails.londonBoroughTookPlace.options.bromley"),
      },
      {
        value: "camden",
        label: tk("detentionDetails.londonBoroughTookPlace.options.camden"),
      },
      {
        value: "cityOfWestminster",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.cityOfWestminster",
        ),
      },
      {
        value: "croydon",
        label: tk("detentionDetails.londonBoroughTookPlace.options.croydon"),
      },
      {
        value: "ealing",
        label: tk("detentionDetails.londonBoroughTookPlace.options.ealing"),
      },
      {
        value: "enfield",
        label: tk("detentionDetails.londonBoroughTookPlace.options.enfield"),
      },
      {
        value: "greenwich",
        label: tk("detentionDetails.londonBoroughTookPlace.options.greenwich"),
      },
      {
        value: "hackney",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hackney"),
      },
      {
        value: "hammersmithAndFulham",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.hammersmithAndFulham",
        ),
      },
      {
        value: "haringey",
        label: tk("detentionDetails.londonBoroughTookPlace.options.haringey"),
      },
      {
        value: "harrow",
        label: tk("detentionDetails.londonBoroughTookPlace.options.harrow"),
      },
      {
        value: "havering",
        label: tk("detentionDetails.londonBoroughTookPlace.options.havering"),
      },
      {
        value: "hillingdon",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hillingdon"),
      },
      {
        value: "hounslow",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hounslow"),
      },
      {
        value: "islington",
        label: tk("detentionDetails.londonBoroughTookPlace.options.islington"),
      },
      {
        value: "kensingtonAndChelsea",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.kensingtonAndChelsea",
        ),
      },
      {
        value: "kingstonUponThames",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.kingstonUponThames",
        ),
      },
      {
        value: "lambeth",
        label: tk("detentionDetails.londonBoroughTookPlace.options.lambeth"),
      },
      {
        value: "lewisham",
        label: tk("detentionDetails.londonBoroughTookPlace.options.lewisham"),
      },
      {
        value: "merton",
        label: tk("detentionDetails.londonBoroughTookPlace.options.merton"),
      },
      {
        value: "newham",
        label: tk("detentionDetails.londonBoroughTookPlace.options.newham"),
      },
      {
        value: "redbridge",
        label: tk("detentionDetails.londonBoroughTookPlace.options.redbridge"),
      },
      {
        value: "richmondUponThames",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.richmondUponThames",
        ),
      },
      {
        value: "southwark",
        label: tk("detentionDetails.londonBoroughTookPlace.options.southwark"),
      },
      {
        value: "sutton",
        label: tk("detentionDetails.londonBoroughTookPlace.options.sutton"),
      },
      {
        value: "towerHamlets",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.towerHamlets",
        ),
      },
      {
        value: "walthamForest",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.walthamForest",
        ),
      },
      {
        value: "wandsworth",
        label: tk("detentionDetails.londonBoroughTookPlace.options.wandsworth"),
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "locationType", // 136-detention-5.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "railwayLines" as const,
          "hospitalWards" as const,
          "placeOfHeight" as const,
          "policeStations" as const,
          "offices" as const,
          "school" as const,
          "hotel" as const,
          "nonResidentialRestricted" as const,
          "emergencyDepartment" as const,
          "hospitalWardsPhysical" as const,
          "inpatientWard" as const,
          "bridgeOverWater" as const,
          "bridgeOverRoad" as const,
          "street" as const,
          "park" as const,
          "noneOfTheAbove" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.locationType.title"),
    requiredLabel: tk("detentionDetails.locationType.required"),
    options: [
      {
        value: "railwayLines",
        label: tk("detentionDetails.locationType.railwayLines"),
      },
      {
        value: "hospitalWards",
        label: tk("detentionDetails.locationType.hospitalWards"),
      },
      {
        value: "placeOfHeight",
        label: tk("detentionDetails.locationType.placeOfHeight"),
      },
      {
        value: "policeStations",
        label: tk("detentionDetails.locationType.policeStations"),
      },
      {
        value: "offices",
        label: tk("detentionDetails.locationType.offices"),
      },
      {
        value: "school",
        label: tk("detentionDetails.locationType.school"),
      },
      {
        value: "hotel",
        label: tk("detentionDetails.locationType.hotel"),
      },
      {
        value: "nonResidentialRestricted",
        label: tk("detentionDetails.locationType.nonResidentialRestricted"),
      },
      {
        value: "emergencyDepartment",
        label: tk("detentionDetails.locationType.emergencyDepartment"),
      },
      {
        value: "hospitalWardsPhysical",
        label: tk("detentionDetails.locationType.hospitalWardsPhysical"),
      },
      {
        value: "inpatientWard",
        label: tk("detentionDetails.locationType.inpatientWard"),
      },
      {
        value: "bridgeOverWater",
        label: tk("detentionDetails.locationType.bridgeOverWater"),
      },
      {
        value: "bridgeOverRoad",
        label: tk("detentionDetails.locationType.bridgeOverRoad"),
      },
      {
        value: "street",
        label: tk("detentionDetails.locationType.street"),
      },
      {
        value: "park",
        label: tk("detentionDetails.locationType.park"),
      },
      {
        value: "noneOfTheAbove",
        label: tk("detentionDetails.locationType.noneOfTheAbove"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmCustodySuite", // 136-detention-5.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "holborn" as const,
          "islington" as const,
          "cxFirstFloor" as const,
          "stokeNewington" as const,
          "woodGreen" as const,
          "bethnalGreen" as const,
          "barkingAndDagenhamFreshwharf" as const,
          "romford" as const,
          "forestGate" as const,
          "leyton" as const,
          "colindale" as const,
          "wembley" as const,
          "heathrow" as const,
          "croydon" as const,
          "brixton" as const,
          "wandsworth" as const,
          "lewishamPfi" as const,
          "bexleyheath" as const,
          "bromleyPfi" as const,
          "walworth" as const,
          "acton" as const,
          "hammersmith" as const,
          "kingston" as const,
          "edmonton" as const,
          "kentishTown" as const,
          "cxGroundFloor" as const,
          "ilford" as const,
          "harrow" as const,
          "suttonPfi" as const,
          "plumstead" as const,
          "peckham" as const,
          "hounslow" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.confirmCustodySuite.title"),
    requiredLabel: tk("detentionDetails.confirmCustodySuite.required"),
    options: [
      {
        value: "acton",
        label: tk("detentionDetails.confirmCustodySuite.options.acton"),
      },
      {
        value: "barkingAndDagenhamFreshwharf",
        label: tk(
          "detentionDetails.confirmCustodySuite.options.barkingAndDagenhamFreshwharf",
        ),
      },
      {
        value: "bexleyheath",
        label: tk("detentionDetails.confirmCustodySuite.options.bexleyheath"),
      },
      {
        value: "bethnalGreen",
        label: tk("detentionDetails.confirmCustodySuite.options.bethnalGreen"),
      },
      {
        value: "brixton",
        label: tk("detentionDetails.confirmCustodySuite.options.brixton"),
      },
      {
        value: "bromleyPfi",
        label: tk("detentionDetails.confirmCustodySuite.options.bromleyPfi"),
      },
      {
        value: "colindale",
        label: tk("detentionDetails.confirmCustodySuite.options.colindale"),
      },
      {
        value: "cxFirstFloor",
        label: tk("detentionDetails.confirmCustodySuite.options.cxFirstFloor"),
      },
      {
        value: "cxGroundFloor",
        label: tk("detentionDetails.confirmCustodySuite.options.cxGroundFloor"),
      },
      {
        value: "croydon",
        label: tk("detentionDetails.confirmCustodySuite.options.croydon"),
      },
      {
        value: "edmonton",
        label: tk("detentionDetails.confirmCustodySuite.options.edmonton"),
      },
      {
        value: "forestGate",
        label: tk("detentionDetails.confirmCustodySuite.options.forestGate"),
      },
      {
        value: "harrow",
        label: tk("detentionDetails.confirmCustodySuite.options.harrow"),
      },
      {
        value: "hammersmith",
        label: tk("detentionDetails.confirmCustodySuite.options.hammersmith"),
      },
      {
        value: "heathrow",
        label: tk("detentionDetails.confirmCustodySuite.options.heathrow"),
      },
      {
        value: "holborn",
        label: tk("detentionDetails.confirmCustodySuite.options.holborn"),
      },
      {
        value: "hounslow",
        label: tk("detentionDetails.confirmCustodySuite.options.hounslow"),
      },
      {
        value: "ilford",
        label: tk("detentionDetails.confirmCustodySuite.options.ilford"),
      },
      {
        value: "islington",
        label: tk("detentionDetails.confirmCustodySuite.options.islington"),
      },
      {
        value: "kingston",
        label: tk("detentionDetails.confirmCustodySuite.options.kingston"),
      },
      {
        value: "kentishTown",
        label: tk("detentionDetails.confirmCustodySuite.options.kentishTown"),
      },
      {
        value: "leyton",
        label: tk("detentionDetails.confirmCustodySuite.options.leyton"),
      },
      {
        value: "lewishamPfi",
        label: tk("detentionDetails.confirmCustodySuite.options.lewishamPfi"),
      },
      {
        value: "peckham",
        label: tk("detentionDetails.confirmCustodySuite.options.peckham"),
      },
      {
        value: "plumstead",
        label: tk("detentionDetails.confirmCustodySuite.options.plumstead"),
      },
      {
        value: "romford",
        label: tk("detentionDetails.confirmCustodySuite.options.romford"),
      },
      {
        value: "stokeNewington",
        label: tk(
          "detentionDetails.confirmCustodySuite.options.stokeNewington",
        ),
      },
      {
        value: "suttonPfi",
        label: tk("detentionDetails.confirmCustodySuite.options.suttonPfi"),
      },
      {
        value: "wandsworth",
        label: tk("detentionDetails.confirmCustodySuite.options.wandsworth"),
      },
      {
        value: "walworth",
        label: tk("detentionDetails.confirmCustodySuite.options.walworth"),
      },
      {
        value: "wembley",
        label: tk("detentionDetails.confirmCustodySuite.options.wembley"),
      },
      {
        value: "woodGreen",
        label: tk("detentionDetails.confirmCustodySuite.options.woodGreen"),
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.locationType?.value === "policeStations",
  })
  .withFieldConditionallyVisible({
    fieldName: "custodyNumber", // 136-detention-5.4
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.locationType?.value === "policeStations",
    label: tk("detentionDetails.custodyNumber"),
    requiredLabel: tk("detentionDetails.custodyNumberRequired"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "custodyNumberDescriptionBanner", // 136-detention-5.5
    type: "banner",
    bannerType: "info",
    body: tk("detentionDetails.custodyNumberDescriptionBanner"),
    isVisible: ({ formValues }) =>
      formValues.locationType?.value === "policeStations",
  })
  .withFieldAlwaysVisible({
    fieldName: "incidentAndLocationSameLocation", // 136-detention-6.1
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("detentionDetails.incidentAndLocationSameLocation"),
    requiredLabel: tk(
      "detentionDetails.originalPoliceIncidentSameLocationRequired",
    ),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "incidentLocation", // 136-detention-6.2
    type: "location",
    ...new FieldBuilder().withSchema(LocationSchema).build(),
    label: tk("detentionDetails.incidentLocation"),
    requiredLabel: tk("detentionDetails.incidentLocationRequired"),
    isVisible: ({ formValues }) =>
      formValues.incidentAndLocationSameLocation?.value === "no",
  })
  .withFieldConditionallyVisible({
    fieldName: "commandUnit", // 136-detention-7.1
    type: "organisation",
    filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
    ...new FieldBuilder()
      .withSchema(commandUnitSchema)
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.commandUnit"),
    requiredLabel: tk("detentionDetails.commandUnitRequired"),
    isVisible: ({ formValues }) =>
      formValues.incidentAndLocationSameLocation?.value === "no",
  })
  .withFieldConditionallyVisible({
    fieldName: "londonBoroughIncidentTookPlace", // 136-detention-7.2
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "barkingAndDagenham" as const,
          "barnet" as const,
          "bexley" as const,
          "brent" as const,
          "bromley" as const,
          "camden" as const,
          "croydon" as const,
          "ealing" as const,
          "enfield" as const,
          "greenwich" as const,
          "hackney" as const,
          "hammersmithAndFulham" as const,
          "haringey" as const,
          "harrow" as const,
          "havering" as const,
          "hillingdon" as const,
          "hounslow" as const,
          "islington" as const,
          "kensingtonAndChelsea" as const,
          "kingstonUponThames" as const,
          "lambeth" as const,
          "lewisham" as const,
          "merton" as const,
          "newham" as const,
          "redbridge" as const,
          "richmondUponThames" as const,
          "southwark" as const,
          "sutton" as const,
          "towerHamlets" as const,
          "walthamForest" as const,
          "wandsworth" as const,
          "cityOfWestminster" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.londonBoroughTookPlace.incident.title"),
    requiredLabel: tk(
      "detentionDetails.londonBoroughTookPlace.incident.required",
    ),
    options: [
      {
        value: "barkingAndDagenham",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.barkingAndDagenham",
        ),
      },
      {
        value: "barnet",
        label: tk("detentionDetails.londonBoroughTookPlace.options.barnet"),
      },
      {
        value: "bexley",
        label: tk("detentionDetails.londonBoroughTookPlace.options.bexley"),
      },
      {
        value: "brent",
        label: tk("detentionDetails.londonBoroughTookPlace.options.brent"),
      },
      {
        value: "bromley",
        label: tk("detentionDetails.londonBoroughTookPlace.options.bromley"),
      },
      {
        value: "camden",
        label: tk("detentionDetails.londonBoroughTookPlace.options.camden"),
      },
      {
        value: "cityOfWestminster",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.cityOfWestminster",
        ),
      },
      {
        value: "croydon",
        label: tk("detentionDetails.londonBoroughTookPlace.options.croydon"),
      },
      {
        value: "ealing",
        label: tk("detentionDetails.londonBoroughTookPlace.options.ealing"),
      },
      {
        value: "enfield",
        label: tk("detentionDetails.londonBoroughTookPlace.options.enfield"),
      },
      {
        value: "greenwich",
        label: tk("detentionDetails.londonBoroughTookPlace.options.greenwich"),
      },
      {
        value: "hackney",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hackney"),
      },
      {
        value: "hammersmithAndFulham",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.hammersmithAndFulham",
        ),
      },
      {
        value: "haringey",
        label: tk("detentionDetails.londonBoroughTookPlace.options.haringey"),
      },
      {
        value: "harrow",
        label: tk("detentionDetails.londonBoroughTookPlace.options.harrow"),
      },
      {
        value: "havering",
        label: tk("detentionDetails.londonBoroughTookPlace.options.havering"),
      },
      {
        value: "hillingdon",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hillingdon"),
      },
      {
        value: "hounslow",
        label: tk("detentionDetails.londonBoroughTookPlace.options.hounslow"),
      },
      {
        value: "islington",
        label: tk("detentionDetails.londonBoroughTookPlace.options.islington"),
      },
      {
        value: "kensingtonAndChelsea",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.kensingtonAndChelsea",
        ),
      },
      {
        value: "kingstonUponThames",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.kingstonUponThames",
        ),
      },
      {
        value: "lambeth",
        label: tk("detentionDetails.londonBoroughTookPlace.options.lambeth"),
      },
      {
        value: "lewisham",
        label: tk("detentionDetails.londonBoroughTookPlace.options.lewisham"),
      },
      {
        value: "merton",
        label: tk("detentionDetails.londonBoroughTookPlace.options.merton"),
      },
      {
        value: "newham",
        label: tk("detentionDetails.londonBoroughTookPlace.options.newham"),
      },
      {
        value: "redbridge",
        label: tk("detentionDetails.londonBoroughTookPlace.options.redbridge"),
      },
      {
        value: "richmondUponThames",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.richmondUponThames",
        ),
      },
      {
        value: "southwark",
        label: tk("detentionDetails.londonBoroughTookPlace.options.southwark"),
      },
      {
        value: "sutton",
        label: tk("detentionDetails.londonBoroughTookPlace.options.sutton"),
      },
      {
        value: "towerHamlets",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.towerHamlets",
        ),
      },
      {
        value: "walthamForest",
        label: tk(
          "detentionDetails.londonBoroughTookPlace.options.walthamForest",
        ),
      },
      {
        value: "wandsworth",
        label: tk("detentionDetails.londonBoroughTookPlace.options.wandsworth"),
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.incidentAndLocationSameLocation?.value === "no",
  })
  .withFieldConditionallyVisible({
    isVisible: ({ formValues }) =>
      formValues.incidentAndLocationSameLocation?.value === "no",
    fieldName: "locationTypeIncident", // 136-detention-7.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "railwayLines" as const,
          "hospitalWards" as const,
          "placeOfHeight" as const,
          "policeStations" as const,
          "offices" as const,
          "school" as const,
          "hotel" as const,
          "nonResidentialRestricted" as const,
          "emergencyDepartment" as const,
          "hospitalWardsPhysical" as const,
          "inpatientWard" as const,
          "bridgeOverWater" as const,
          "bridgeOverRoad" as const,
          "street" as const,
          "park" as const,
          "noneOfTheAbove" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.locationType.title"),
    requiredLabel: tk("detentionDetails.locationType.required"),
    options: [
      {
        value: "railwayLines",
        label: tk("detentionDetails.locationType.railwayLines"),
      },
      {
        value: "hospitalWards",
        label: tk("detentionDetails.locationType.hospitalWards"),
      },
      {
        value: "placeOfHeight",
        label: tk("detentionDetails.locationType.placeOfHeight"),
      },
      {
        value: "policeStations",
        label: tk("detentionDetails.locationType.policeStations"),
      },
      {
        value: "offices",
        label: tk("detentionDetails.locationType.offices"),
      },
      {
        value: "school",
        label: tk("detentionDetails.locationType.school"),
      },
      {
        value: "hotel",
        label: tk("detentionDetails.locationType.hotel"),
      },
      {
        value: "nonResidentialRestricted",
        label: tk("detentionDetails.locationType.nonResidentialRestricted"),
      },
      {
        value: "emergencyDepartment",
        label: tk("detentionDetails.locationType.emergencyDepartment"),
      },
      {
        value: "hospitalWardsPhysical",
        label: tk("detentionDetails.locationType.hospitalWardsPhysical"),
      },
      {
        value: "inpatientWard",
        label: tk("detentionDetails.locationType.inpatientWard"),
      },
      {
        value: "bridgeOverWater",
        label: tk("detentionDetails.locationType.bridgeOverWater"),
      },
      {
        value: "bridgeOverRoad",
        label: tk("detentionDetails.locationType.bridgeOverRoad"),
      },
      {
        value: "street",
        label: tk("detentionDetails.locationType.street"),
      },
      {
        value: "park",
        label: tk("detentionDetails.locationType.park"),
      },
      {
        value: "noneOfTheAbove",
        label: tk("detentionDetails.locationType.noneOfTheAbove"),
      },
    ],
  })
  .withPage([
    "userIsAttendingOfficer", // 136-detention-1.1
    "createdOnBehalfOfAnotherOfficerWithShoulderNumber", // 136-detention-1.2
    "createdOnBehalfOfAnotherOfficerBanner", // 136-detention-1.3
  ])
  .withPage([
    "dateTimeOfDetention", // 136-detention-2.1
  ])
  .withPage([
    "locationDetentionTookPlace", // 136-detention-3.1
    "confirmPersonAddress", // 136-detention-3.2
    "incidentLocationBanner", // 136-detention-3.3
  ])
  .withPage([
    "orgResponsibleForDetention", // 136-detention-4.1
    "londonBoroughDetentionTookPlace", // 136-detention-4.2
  ])
  .withPage([
    "locationType", // 136-detention-5.1
    "confirmCustodySuite", // 136-detention-5.3
    "custodyNumber", // 136-detention-5.4
    "custodyNumberDescriptionBanner", // 136-detention-5.5
  ])
  .withPage([
    "incidentAndLocationSameLocation", // 136-detention-6.1
    "incidentLocation", // 136-detention-6.2
  ])
  .withPage([
    "commandUnit", // 136-detention-7.1
    "londonBoroughIncidentTookPlace", // 136-detention-7.2
    "locationTypeIncident", // 136-detention-7.3
  ])
  .build({ t });

export type DetentionDetailsFormData = ReturnType<
  typeof formSpecification
>["formType"];
